<script setup lang="ts">
import {
  ElMessage,
  ElMessageBox,
  UploadProps,
  UploadFile as UploadUserFileEp,
} from "element-plus";
import { computed, inject, onMounted, ref, watch } from "vue";
import {
  formResponseInjectKey,
  FormResponseState,
} from "~/NewFormBuilder/composables/useFormResponse";

import {
  FileUploadAnswerValue,
  FileUploadFormElement,
  FormElementType,
} from "~/api";
import { getEnvConfig } from "~/services/appConfig";
import { apiClient } from "~/utils/apiClient";
import { emptyGuid } from "~/utils/constants";
import { DefaultFormComponentProps } from "./FormComponentRenderer.vue";

import { authStore } from "~/stores/authStore";
const authToken = ref<string>();

import { useMyInfoStore } from "~/stores/MyInfoStore";
const myInfoStore = useMyInfoStore();

import { usePdfLoadingStore } from "~/stores/pdfLoadingStore";
import { useIsInPdfMode } from "~/stores/pdfModeStore";
import { showError } from "~/utils/handleAPIError";
const pdfLoadingStore = usePdfLoadingStore();

const pdfDebugLogElem = document.getElementById("pdfDebugLog");

interface CustomUploadUserFileEp extends UploadUserFileEp {
  fileGuid?: string;
}

const props = defineProps<DefaultFormComponentProps<FileUploadFormElement>>();

const { useAnswer, formResponse, refreshRecentlyUpdatedAnswers } = inject(
  formResponseInjectKey,
  // Cast the type since we know it is always provided. For more info see:
  // https://vuejs.org/guide/typescript/composition-api.html#typing-provide-inject
) as FormResponseState;

const answer = useAnswer<FormElementType.FILE_UPLOAD>(
  props.modelValue,
  async (newVal, element, fr) => {
    // override the default api func logic
    if (newVal === null || newVal === undefined) {
      await apiClient.formResponseFileUpload.deleteFileUpload(
        fr.guid,
        element.guid,
        element.repeaterGuid ?? "",
        fr.formDefinitionGuid,
      );
      return {
        status: "deleted",
        value: null,
      };
    }

    return Promise.resolve({
      status: "updated",
      value: newVal,
    });
  },
);

// we only have one file, so just return it in an array to make element-plus happy
const fileList = ref<CustomUploadUserFileEp[]>(
  answer.value?.map((f) => ({
    name: f.fileName ?? "Unknown",
    fileGuid: f.fileGuid ?? "Unknown",
    status: "success",
    uid: 0,
  })) ?? [],
);

const disabled = computed<boolean>(() => props.isReadOnly || !authToken.value);
const maxFiles = computed<number>(
  () => props.modelValue.properties.maxFiles ?? 1,
);

const apiBase = getEnvConfig().serverUrl;
const repeatableGroupGuid = props.modelValue.repeaterGuid ?? emptyGuid;
const actionUrl = formResponse?.guid
  ? `${apiBase}/api/mwforms/${props.modelValue.formDefinitionGuid}/responses/${formResponse?.guid}/fileupload/${props.modelValue.guid}/${myInfoStore.currentSysOrgGuid}/${repeatableGroupGuid}/SubmitFileUpload`
  : undefined;

const handleSuccess = (res: FileUploadAnswerValue[]) => {
  answer.value = res;
};

const inPdf = useIsInPdfMode();

const getThumbnailURL = async (fileGuid: string): Promise<string | null> => {
  if (!authToken.value) {
    return null;
  }

  try {
    const response = await fetch(`${apiBase}/api/files/thumbnail/${fileGuid}`, {
      headers: {
        Authorization: `Bearer ${authToken.value}`,
      },
    });

    if (!response.ok) {
      return null;
    }

    const blob = await response.blob();

    if (pdfDebugLogElem) {
      pdfDebugLogElem.innerText +=
        "Got thumbnail for fileGuid: " + fileGuid + "\n";
    }

    if (inPdf.value) {
      // If in PDF mode, return as Base64 data url
      const reader = new FileReader();
      reader.readAsDataURL(blob);
      await new Promise((resolve) => {
        reader.onloadend = resolve;
      });
      // Return the Base64 data URL
      if (reader.result) {
        return reader.result as string;
      }
      // If for some reason reader.result is null, return null
      return null;
    }
    // not in PDF mode, convert the blob to a URL
    return URL.createObjectURL(blob);
  } catch (err) {
    showError(`Error generating thumbnail for fileGuid "${fileGuid}":`, err);
    return null;
  }
};

const generateThumbnails = async () => {
  if (pdfDebugLogElem) {
    pdfDebugLogElem.innerText +=
      "Generating thumbnails for " + fileList.value.length + " files\n";
  }

  if (!authToken.value) {
    return;
  }

  await Promise.all(
    fileList.value.map(async (file) => {
      // for PDF files, we'll use a simple image
      if (file.name.toLowerCase().endsWith(".pdf")) {
        // Use relative path so it resolves correctly via the <base> element
        // (important for sub-path deployments like /HAVN-Main-QA/ and for the PDF converter)
        file.url = `./images/pdf.png#${file.fileGuid}`;
      } else {
        const thumbnailUrl = await getThumbnailURL(file.fileGuid ?? "");
        if (thumbnailUrl) {
          file.url = thumbnailUrl + "#" + file.fileGuid; // append fileGuid for PDF mode
        }
      }
      if (inPdf.value) {
        pdfLoadingStore.componentLoaded();
      }
    }),
  );
};

watch(
  () => answer.value,
  async () => {
    console.trace("FileUpload.vue: answer.value changed", answer.value);
    //update the filelist with the latest values
    fileList.value = (answer.value ?? []).map((f) => ({
      name: f.fileName ?? "Unknown",
      fileGuid: f.fileGuid ?? "Unknown",
      status: "success",
      uid: 0,
    }));

    if (pdfDebugLogElem) {
      pdfDebugLogElem.innerText +=
        "File list updated with " + fileList.value.length + " files\n";
    }
    await generateThumbnails();
  },
  { immediate: false }, // only run on initial mount,
);

const processFileError = (res: Error, file: CustomUploadUserFileEp) => {
  const serverErrorObj = JSON.parse(res.message);
  // from server model validation
  const uploadError = serverErrorObj.answerValue?.at(0);
  if (uploadError) {
    showError(`Error uploading file "${file.name}": ${uploadError}`);
  } else {
    showError(`Unknown error uploading file "${file.name}"`);
  }
};

const handleDownload = async (uploadFile: CustomUploadUserFileEp) => {
  if (!answer.value || !authToken.value) {
    showError("Failed to download: Too early");
  }
  if (myInfoStore.currentSysOrgGuid === formResponse?.sysOrgGuid) {
    try {
      const response = await fetch(
        `${apiBase}/api/files/${uploadFile.fileGuid}`,
        {
          headers: {
            Authorization: `Bearer ${authToken.value}`,
          },
        },
      );

      if (!response.ok) {
        throw new Error("Network response was not ok");
      }

      const blob = await response.blob();
      const blobUrl = URL.createObjectURL(blob);
      const downloadFileName = uploadFile.name;

      const downloadNode = document.createElement("a");
      downloadNode.setAttribute("href", blobUrl);
      downloadNode.setAttribute("download", downloadFileName);
      downloadNode.click();
      downloadNode.remove();
      ElMessage.success({
        message: "Downloaded",
        grouping: true,
      });
    } catch (err) {
      showError("Error Downloading File", err);
    }
  }
};

const beforeRemove: UploadProps["beforeRemove"] = (uploadFile) => {
  return ElMessageBox.confirm(
    `Are you sure you want to permanently delete this file: '${uploadFile.name}'?`,
    `Delete '${uploadFile.name}'`,
    {
      confirmButtonText: "Yes",
      cancelButtonText: "Cancel",
      closeOnPressEscape: false,
      closeOnClickModal: false,
      showClose: false,
      type: "warning",
    },
  ).then(
    () => true,
    () => false,
  );
};

const handleRemove = async (uploadFileToRemove: CustomUploadUserFileEp) => {
  if (!answer.value) {
    showError("Developer Error: tried to delete a file upload when none exist");
    return;
  } else if (!formResponse) {
    showError(
      "Developer Error: tried to delete a file upload without a form response",
    );
    return;
  }

  await apiClient.formResponseFileUpload.deleteFileUpload(
    formResponse.guid,
    props.modelValue.guid,
    props.modelValue.repeaterGuid ?? emptyGuid,
    formResponse.formDefinitionGuid,
    uploadFileToRemove.fileGuid,
  );

  const updatedFiles = answer.value.filter(
    (f) => f.fileGuid !== uploadFileToRemove.fileGuid,
  );
  answer.value = updatedFiles;

  fileList.value = fileList.value.filter(
    (f) => f.fileGuid !== uploadFileToRemove.fileGuid,
  );

  await refreshRecentlyUpdatedAnswers();
};

onMounted(async () => {
  authToken.value = (await authStore.getAccessTokenAsync()) ?? undefined;
  if (pdfDebugLogElem) {
    pdfDebugLogElem.innerText +=
      "onMounted in FileUpload " + props.modelValue.guid + "\n";
  }

  // add registerComponent for each file
  fileList.value.forEach((file) => {
    if (pdfDebugLogElem) {
      pdfDebugLogElem.innerText +=
        "Registering component for file: " + file.fileGuid + "\n";
    }
    if (inPdf.value) {
      pdfLoadingStore.componentBeingLoaded();
    }
  });

  // register the component as loaded if there are no files
  await generateThumbnails();
});
</script>

<template id="form-files-component">
  <div class="FileUploadComponent" :class="{ inPdf: inPdf }">
    <slot name="label" :element="modelValue" />

    <div class="files-body">
      <el-upload
        :action="actionUrl"
        :file-list="fileList"
        :on-success="handleSuccess"
        :on-error="processFileError"
        :on-preview="handleDownload"
        :disabled="disabled"
        :limit="maxFiles"
        :accept="props.modelValue.properties.accept ?? undefined"
        list-type="picture"
        class="files-upload-droppable"
        :headers="{ Authorization: `Bearer ${authToken}` }"
        :class="{
          'files-upload-droppable--answered':
            fileList && fileList.length >= maxFiles,
          readOnly: disabled,
        }"
        drag
        :before-remove="beforeRemove"
        @remove="handleRemove"
      >
        <el-icon class="el-icon--upload"><el-icon-upload-filled /></el-icon>
        <div class="el-upload__text">
          Drop file here or <em>click to upload</em>

          <div
            v-if="fileList && fileList.length >= maxFiles"
            class="el-upload__tip"
          >
            files less than 10mb
          </div>
        </div>
      </el-upload>
      <div v-if="fileList.length === 0 && isReadOnly" class="noFiles">
        No files uploaded.
      </div>
    </div>
  </div>
</template>

<style lang="less">
.FileUploadComponent {
  display: flex;
  flex-direction: column;
  gap: 10px;

  .files-body {
    position: relative;
    display: flex;
    page-break-inside: auto; // allow it
    flex-wrap: wrap;
    justify-content: flex-start;
    .files-upload-droppable {
      width: 100%;
      max-width: 640px;
      page-break-inside: auto; // allow it

      .el-upload-list__item {
        transition: none !important;
      }
      .el-upload-list {
        width: fit-content;
        min-width: calc(30%);
      }

      .el-upload-list--picture .el-upload-list__item-thumbnail {
        max-height: 70px;
        max-width: 70px;
        height: auto;
        width: auto;
        object-fit: contain;
      }
    }
    .readOnly {
      .el-upload-dragger {
        display: none;
      }
      .el-upload-list {
        margin-left: 25px;
        page-break-inside: auto; // allow it
      }
      .el-upload-list__item {
        background-color: var(--el-color-info-light-9);
      }
      .el-upload-list__item-status-label {
        display: none;
      }
    }
  }

  .files-upload-droppable--answered > div.el-upload {
    display: none;
  }

  .files-tips {
    position: absolute;
    bottom: 6px;
    right: 10px;
    color: #666;
    font-size: 12px;
  }

  &.inPdf {
    .el-upload-list {
      display: flex;
      flex-direction: column;
      flex-wrap: wrap;
      gap: 10px;
      padding: 0;
      margin: 0 1em;
      position: relative;
    }
    .files-upload-droppable {
      display: flex;
      flex-direction: column;
    }

    .files-body .readOnly .el-upload-list__item {
      background-color: transparent;
      width: fit-content;

      .el-upload-list__item-info {
        margin-left: 1em;
      }
    }
    .noFiles {
      border: 1px dashed var(--el-border-color);
      border-radius: 6px;
      box-sizing: border-box;
      padding: 0 10px;
    }
  }
}
</style>
